import { BackWeightFields } from '@/library/BackWeightHelper';
import { useState } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useToast } from '@/context/ToastContext';
import EditIron from './EditIron';
import AddIronToSetModal from './AddIronToSetModal';
import FinalHeadWeightModal from './FinalHeadWeightModal';
import { Accordion, AccordionShell, ControlledAccordion } from '@/common/Accordion';
import InTheBag from '@/common/InTheBag';
import Tile from '@/common/Tile';
import { TourBagService } from '@/service';
import { TourBagIronDetail, TourBagIronSet, TourBagMstrPayload } from '@/api/models';
import { isEmptyTile, isMobileResolution, normalizeString, createEmptyTourBagIronDetail, createDeletedTourBagIronDetail, createEmptyTourBag } from '@/utils';
import { buildIronPayload, buildIronMetadata, buildSetSharedProps } from './helpers';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { isFieldVisible } from '@/library/FieldVisibilityHelper';
import { PageReadability } from '@/library/TourPageSettingsHelper';
import { IUseEditIronResult } from './useEditIron';
import { calculateFinalHeadWeight } from './caluclateFinalHeadWeight';
import clubStyles from '../Club.module.scss';
import setStyles from '../Set.module.scss';
import { Action } from '@/constants';
import OfflineModal from '@/common/OfflineModal';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import Linkify from 'linkify-react';
import PhotoCaptureSection from '@/common/PhotoCaptureSection';

const module = 'Iron';

export interface IIronClub extends BackWeightFields {
  isActive: boolean;
  ironHand: string;
  ironFamilyId: number;
  ironHeadModelId: number;
  ironClubTypeId: number;
  loft: string;
  lie: string;
  swingWt: string;
  measured: boolean;
  ironShaftId: number;
  ironShaftLength: string;
  ironShaftCosmetic: string;
  ironGripType: string;
  ironGripSizeId: number;
  ironGripTape: string;
  ironRoundRib: 'Round' | 'Rib';
  ironLogo: 'Up' | 'Down';
  notes: string;
  headWeight: number | null;
  drill: number | null;
  tipPlug: number | null;
  glueWeight: number | null;
  leadTape: number | null;
}

const emptyTourBagIronDetail = createEmptyTourBagIronDetail();

interface IRenderIronProps {
  iron: TourBagIronDetail;
  pageReadability: PageReadability | undefined;
  /* eslint-disable no-unused-vars */
  onFinalHWClick?: (iron: TourBagIronDetail) => void;
  /* eslint-enable no-unused-vars */
}

const renderIron = ({ iron, pageReadability, onFinalHWClick }: IRenderIronProps) => {
  const loftValue = `${iron.loftDigit}${iron.loftFraction}`;
  const lieValue = `${iron.lieDigit}${iron.lieFraction}`;
  const swingWt = `${iron.swingWtPrefixName}${iron.swingWtSuffixName}`;
  // Shaft label includes manufacturer + shaft name
  const shaft = iron.shaftName || '';
  const gripName = normalizeString(iron.gripType);
  const notes = normalizeString(iron.notes);
  const finalHeadWeight = calculateFinalHeadWeight({
    headWeight: iron.headWeight,
    drill: iron.drill,
    tipPlug: iron.tipPlug,
    glueWeight: iron.glueWeight,
    leadTape: iron.leadTape,
  });

  return (
    <>
      <div className={clubStyles.tileRow}>
        {isFieldVisible(module, 'hand', pageReadability) && <Tile label="Hand" value={iron.hand} />}
        {isFieldVisible(module, 'familyName', pageReadability) && <Tile label="Family" value={normalizeString(iron.familyName)} variant="large" />}
        {isFieldVisible(module, 'loft', pageReadability) && <Tile label="Loft" value={normalizeString(loftValue)} />}
        {isFieldVisible(module, 'lie', pageReadability) && <Tile label="Lie" value={normalizeString(lieValue)} />}
        {isFieldVisible(module, 'swingWt', pageReadability) && <Tile label="Swing Wt" value={normalizeString(swingWt)} variant="large" />}
        {isFieldVisible(module, 'finalHW', pageReadability) && (
          <>
            {onFinalHWClick && (
              <span className={clubStyles.finalHWWrapper} role="button" onClick={() => onFinalHWClick(iron)}>
                <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />
              </span>
            )}
            {!onFinalHWClick && <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />}
          </>
        )}
      </div>
      {isFieldVisible(module, 'shaftName', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Shaft</div>
            <div className={`${clubStyles.categoryTitle} ${isEmptyTile(shaft) ? clubStyles.emptyValue : ''}`}>{shaft}</div>
          </div>
          <div className={clubStyles.tileRow}>
            {isFieldVisible(module, 'isShaftLengthFinished', pageReadability) && <Tile label="Measured" value={iron.isShaftLengthFinished ? 'Finished' : 'Cut'} />}
            {isFieldVisible(module, 'shaftLengthDigit', pageReadability) && <Tile label="Length" value={normalizeString(`${iron.shaftLengthDigit} ${iron.shaftLengthFraction}`)} />}
            {isFieldVisible(module, 'shaftCosmetic', pageReadability) && <Tile label="Shaft Cosmetic" value={normalizeString(iron.shaftCosmetic)} variant="large" />}
          </div>
        </>
      )}
      {isFieldVisible(module, 'ironGripType', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Grip</div>
            <div className={`${clubStyles.categoryTitle} ${isEmptyTile(gripName) ? clubStyles.emptyValue : ''}`}>{gripName}</div>
          </div>
          <div className={clubStyles.tileRow}>
            {isFieldVisible(module, 'ironGripSizeId', pageReadability) && <Tile label="Grip Size" value={normalizeString(iron.gripSize)} variant="large" />}
            {isFieldVisible(module, 'ironRoundRib', pageReadability) && <Tile label="Round/Rib" value={normalizeString(iron.rndRib)} variant="large" />}
            {isFieldVisible(module, 'ironLogo', pageReadability) && <Tile label="Logo" value={normalizeString(iron.logo)} variant="large" />}
            {isFieldVisible(module, 'ironGripTape', pageReadability) && <Tile label="Grip Tape" value={normalizeString(iron.tape)} variant="large" />}
          </div>
        </>
      )}
      {isFieldVisible(module, 'notes', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Notes</div>
          </div>
          <div className={clubStyles.tileRow}>
            <div className={`${clubStyles.notes} ${isEmptyTile(notes) ? clubStyles.emptyValue : ''}`}>
              <Linkify options={{ target: '_blank' }}>{notes}</Linkify>
            </div>
          </div>
        </>
      )}
      <PhotoCaptureSection playerId={iron.playerId} ownerRowGuid={iron.rowGuid} headTypeId={iron.headTypeId} imageMetadata={iron.imageMetadata} />
    </>
  );
};

interface IIronReadOnlyProps {
  ironClubs: TourBagIronDetail[];
  currentAccordionId: number;
  showCheckbox: boolean;
  /* eslint-disable no-unused-vars */
  isClubSelected: (id: number) => boolean;
  handleOpenAccordion: (id: number) => void;
  handleCheckboxEvent: (id: number, isChecked: boolean) => void;
  pageReadability?: PageReadability | undefined;
  /* eslint-enable no-unused-vars */
}

export const IronReadOnly = ({ ironClubs, currentAccordionId, showCheckbox, isClubSelected, handleOpenAccordion, handleCheckboxEvent, pageReadability }: IIronReadOnlyProps) => {
  const [finalHWIron, setFinalHWIron] = useState<TourBagIronDetail | null>(null);

  return (
    <>
      {ironClubs
        ?.sort((a, b) => a.headModelName.localeCompare(b.headModelName))
        ?.sort((a, b) => Number(b.isActive) - Number(a.isActive))
        .map((iron) => {
          // Old system headModelName included familyName but now we don't, we check to remove duplicated familyName
          const ironModel = iron.headModelName.startsWith(iron.familyName) ? iron.headModelName : `${iron.familyName} ${iron.headModelName}`;
          return (
            <ControlledAccordion
              key={iron.tourBagIronDetailId}
              id={iron.tourBagIronDetailId}
              header={
                <>
                  <div className={setStyles.headerRow}>
                    <span className={setStyles.ironInfo}>
                      <span className={setStyles.ironModel}>{pageReadability === 'limitedView' && iron.headModelAlternateName ? iron.headModelAlternateName : ironModel}</span>
                      {isFieldVisible(module, 'ironClubName', pageReadability) && (
                        <>
                          <span className={setStyles.dot}>·</span>
                          <span className={setStyles.ironType}>{iron.ironClubName}</span>
                        </>
                      )}
                    </span>
                    {!isMobileResolution && <InTheBag isActive={iron.isActive} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                  </div>
                  {isMobileResolution && <InTheBag isActive={iron.isActive} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                </>
              }
              isOpen={currentAccordionId === iron.tourBagIronDetailId}
              showCheckbox={showCheckbox}
              checked={isClubSelected(iron.tourBagIronDetailId)}
              handleOpenAccordion={() => handleOpenAccordion(iron.tourBagIronDetailId)}
              handleCheckboxEvent={handleCheckboxEvent}
            >
              {renderIron({ iron, pageReadability, onFinalHWClick: setFinalHWIron })}
            </ControlledAccordion>
          );
        })}
      {finalHWIron && <FinalHeadWeightModal showFinalHWModal closeFinalHWModal={() => setFinalHWIron(null)} iron={finalHWIron} />}
    </>
  );
};

/* eslint-disable no-unused-vars */
interface IIronListProps extends Omit<IUseEditIronResult, 'ironClubData'> {
  showClubs: boolean;
  setShowSpinner: (state: boolean) => void;
  setIronToCreate: (iron: TourBagIronDetail | null) => void;
  ironToCreate: TourBagIronDetail | null;
  showCreateIron: boolean;
  setShowCreateIron: (state: boolean) => void;
  ironToEdit: TourBagIronDetail | null;
  setIronToEdit: (iron: TourBagIronDetail | null) => void;
  handleIronForHistory: (iron: TourBagIronDetail | null) => void;
}
/* eslint-enable no-unused-vars */

function IronList({
  showClubs,
  setShowSpinner,
  loftDropdownData,
  lieDropdownData,
  ironStaticDropdownData,
  ironDynamicDropdownData,
  shaftDropdownData,
  shaftLengthDropdownData,
  swingWtDropdownData,
  handDropdownData,
  roundRibDropdownData,
  logoDropdownData,
  setIronToCreate,
  handleIronForHistory,
  ironToCreate,
  showCreateIron,
  setShowCreateIron,
  ironToEdit,
  setIronToEdit,
}: IIronListProps) {
  const { tourBag, player, updateTourBag, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const { showToast } = useToast();
  const [showAddIronToSetModal, setShowAddIronToSetModal] = useState(false);
  const [ironToAddToSet, setIronToAddToSet] = useState<TourBagIronDetail | null>(null);
  const [showFinalHWModal, setShowFinalHWModal] = useState(false);
  const [finalHWIron, setFinalHWIron] = useState<TourBagIronDetail | null>(null);
  const ironBag = tourBag?.find((bag) => bag.headTypeDescription === 'Iron');
  const ironSets = ironBag?.tourBagIronSets || [];
  const ironClubs = ironBag?.tourBagIronDetail || [];
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [showPostEditOfflineModal, setShowPostEditOfflineModal] = useState(false);
  const isOnline = useOnlineStatus();
  const [ironToEditCached, setIronToEditCached] = useState<TourBagIronDetail | null>(null);

  const handleAddNewIron = async (ironClub: IIronClub) => {
    const tourBag = ironBag ?? createEmptyTourBag('Iron', player!);
    // if (!ironBag) throw new Error('Missing iron bag');
    // if (!ironClubs) throw new Error('Missing iron clubs');
    if (isOnline) {
      setShowSpinner(true);
    }

    //need next itemNo in collection
    let itemNo = 1;
    if (tourBag.tourBagIronDetail.length > 0) {
      itemNo = Math.max(...tourBag.tourBagIronDetail.map((tb) => tb.itemNo)) + 1;
    }
    // build new iron writing the new values over the default empty bag detail
    const newIron: TourBagIronDetail = {
      ...emptyTourBagIronDetail,
      ...buildIronPayload({
        ironClub,
        loftDropdownData,
        lieDropdownData,
        ironStaticDropdownData,
        ironDynamicDropdownData,
        shaftDropdownData,
        shaftLengthDropdownData,
        swingWtDropdownData,
      }),
      itemNo,
      ...buildIronMetadata({ player }),
      tourBagMstrId: ironBag?.tourBagMstrId || emptyTourBagIronDetail.tourBagMstrId,
    };
    const updatedIrons: TourBagIronDetail[] = [...ironClubs, newIron];
    const updatedBag: TourBagMstrPayload = {
      ...tourBag,
      tourBagIronDetail: updatedIrons,
    };

    try {
      const action: Action = 'CREATE';
      await updateTourBag(updatedBag, action, newIron.tourBagIronDetailId);
      setIronToCreate(null); // exit creation upon success
      setShowCreateIron(false); // hide add new iron component
    } catch (error) {
      showToast({ message: 'Failed to Create Iron' });
      console.error('Failed to Edit Iron club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      if (!isOnline) {
        setShowPostEditOfflineModal(true);
      } else {
        showToast({ message: 'Iron Created Successfully', showCheckmark: true });
        setShowSpinner(false);
      }
    }
  };

  const handleEditedIron = async (ironClub: IIronClub) => {
    if (!ironBag) throw new Error('Missing iron bag');
    if (!ironClubs) throw new Error('Missing iron clubs');
    if (isOnline) {
      setShowSpinner(true);
    }

    const updatedIrons: TourBagIronDetail[] = ironClubs.map((iron) => {
      if (ironToEdit?.tourBagIronDetailId === iron.tourBagIronDetailId) {
        const updatedIron = buildIronPayload({
          ironClub,
          loftDropdownData,
          lieDropdownData,
          ironStaticDropdownData,
          ironDynamicDropdownData,
          shaftDropdownData,
          shaftLengthDropdownData,
          swingWtDropdownData,
        });
        return { ...iron, ...updatedIron };
      }
      return iron;
    });
    const updatedBag: TourBagMstrPayload = {
      ...ironBag,
      tourBagMstrId: ironBag?.tourBagMstrId,
      tourBagIronDetail: updatedIrons,
    };

    try {
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, ironToEdit?.tourBagIronDetailId);
      setIronToEdit(null); // exit edition upon success
    } catch (error) {
      showToast({ message: 'Failed to Edit Iron' });
      console.error('Failed to Edit Iron club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      if (!isOnline) {
        setShowPostEditOfflineModal(true);
      } else {
        setShowSpinner(false);
        showToast({ message: 'Iron Edited Successfully', showCheckmark: true });
      }
    }
  };

  const handleClosePostEditOfflineModal = () => {
    setShowPostEditOfflineModal(false);
  };

  const handleDeleteIron = async (tourBagIronDetailId: number) => {
    if (!ironBag) throw new Error('Missing iron bag');
    if (!ironClubs) throw new Error('Missing Iron clubs');

    const tourBag = ironClubs.find((iron) => iron.tourBagIronDetailId === tourBagIronDetailId);
    if (!tourBag) throw new Error('Missing Iron bag to delete');
    setShowSpinner(true);

    const deletedBag = createDeletedTourBagIronDetail(tourBag);
    const updatedIrons = ironClubs.filter((iron) => iron.tourBagIronDetailId !== tourBagIronDetailId);
    const updatedBag: TourBagMstrPayload = {
      ...ironBag,
      tourBagIronDetail: updatedIrons,
    };

    try {
      await TourBagService.saveDeletedTourBag(deletedBag);
      // TODO: Offline not supported yet
      const action: Action = 'DELETE';
      await updateTourBag(updatedBag, action, tourBagIronDetailId);
      showToast({ message: 'Iron Deleted Successfully', showCheckmark: true });
    } catch (error) {
      showToast({ message: 'Failed to Delete Iron' });
      console.error('Failed to Delete Iron club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      setShowSpinner(false);
    }
  };

  const handleAddIronToSet = async (selectedSet: TourBagIronSet) => {
    if (!ironBag) throw new Error('Missing iron bag');
    if (!ironToAddToSet) throw new Error('Missing Iron to Add to Set');

    setShowSpinner(true);

    // Add Set shared props and set id to Iron
    const updatedIron: TourBagIronDetail = { ...ironToAddToSet, ...buildSetSharedProps(selectedSet, ironToAddToSet), tourBagIronSetId: selectedSet.tourBagIronSetId };
    // Add Iron to the set
    const updatedSet = {
      ...selectedSet,
      tourBagIronDetails: [...selectedSet.tourBagIronDetails, updatedIron],
    };
    // Remove Iron from tour bag tourBagIronDetail
    const updatedIronList = ironBag.tourBagIronDetail.filter((tourBagIron) => tourBagIron.tourBagIronDetailId !== ironToAddToSet.tourBagIronDetailId);
    // Add Iron to tourBagIronDetail Iron array, and remove the set id
    const updatedBag: TourBagMstrPayload = {
      ...ironBag,
      tourBagIronDetail: updatedIronList,
      tourBagIronSets: ironSets.map((currentSet) => (currentSet.tourBagIronSetId === selectedSet.tourBagIronSetId ? updatedSet : currentSet)),
    };

    try {
      // TODO: Offline not supported yet
      const action: Action = 'UPDATE';
      await updateTourBag(updatedBag, action, ironToAddToSet.tourBagIronDetailId);
      setIronToAddToSet(null); // reset state
      showToast({ message: 'Iron Added to Set Successfully', showCheckmark: true });
    } catch (error) {
      showToast({ message: 'Failed to Add Iron to Set' });
      console.error('Failed to Remove Iron club values: ', error);
      // TODO: Save error to ErrorLogRepo
      throw error;
    } finally {
      setShowSpinner(false);
    }
  };

  const openOfflineEditModal = (iron: TourBagIronDetail) => {
    setIronToEditCached(iron);
    setShowPreEditOfflineModal(true);
  };

  const showEditFormOfflineModal = () => {
    setShowPreEditOfflineModal(false);
    setIronToEdit(ironToEditCached);
  };

  return (
    <>
      {/* Create/ Copy Iron */}
      {(ironToCreate || showCreateIron) && (
        <AccordionShell>
          <EditIron
            title="Create Club"
            iron={ironToCreate || emptyTourBagIronDetail}
            handDropdownData={handDropdownData}
            loftDropdownData={loftDropdownData}
            lieDropdownData={lieDropdownData}
            ironStaticDropdownData={ironStaticDropdownData}
            ironDynamicDropdownData={ironDynamicDropdownData}
            shaftDropdownData={shaftDropdownData}
            shaftLengthDropdownData={shaftLengthDropdownData}
            swingWtDropdownData={swingWtDropdownData}
            roundRibDropdownData={roundRibDropdownData}
            logoDropdownData={logoDropdownData}
            handleSave={handleAddNewIron}
            handleCancel={() => {
              setIronToCreate(null);
              setShowCreateIron(false);
            }}
          />
        </AccordionShell>
      )}
      {/* Edit Iron */}
      {ironToEdit !== null && (
        <AccordionShell>
          <EditIron
            title="Edit Club"
            iron={ironToEdit}
            handDropdownData={handDropdownData}
            loftDropdownData={loftDropdownData}
            lieDropdownData={lieDropdownData}
            ironStaticDropdownData={ironStaticDropdownData}
            ironDynamicDropdownData={ironDynamicDropdownData}
            shaftDropdownData={shaftDropdownData}
            shaftLengthDropdownData={shaftLengthDropdownData}
            swingWtDropdownData={swingWtDropdownData}
            roundRibDropdownData={roundRibDropdownData}
            logoDropdownData={logoDropdownData}
            handleSave={handleEditedIron}
            handleCancel={() => setIronToEdit(null)}
          />
        </AccordionShell>
      )}
      {/* Iron List */}
      {showClubs &&
        ironClubs
          .sort((a, b) => (a.loftDigit + a.loftFraction).localeCompare(b.loftDigit + b.loftFraction))
          .sort((a, b) => Number(b.isActive) - Number(a.isActive))
          .map((iron, index) => {
            // Old system headModelName included familyName but now we don't, we check to remove duplicated familyName
            let ironHeadModel = iron.headModelName.startsWith(iron.familyName) ? iron.headModelName : `${iron.familyName} ${iron.headModelName}`;
            if (tourPageSettings?.iron === 'limitedView') ironHeadModel = iron.headModelAlternateName || ironHeadModel;

            return (
              <Accordion
                key={iron.tourBagIronDetailId}
                index={index}
                header={
                  <>
                    <div className={setStyles.headerRow}>
                      <span className={setStyles.ironInfo}>
                        <span className={setStyles.ironModel}>{ironHeadModel}</span>
                        <span className={setStyles.dot}>·</span>
                        <span className={setStyles.ironType}>{iron.ironClubName}</span>
                      </span>
                      {!isMobileResolution && <InTheBag isActive={iron.isActive} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                    </div>
                    {isMobileResolution && <InTheBag isActive={iron.isActive} className={`${clubStyles.inTheBag} ${setStyles.inBag}`} />}
                  </>
                }
                showMenu={tourPageSettings?.iron === 'full' && isSyncComplete}
                showHistory={permissions?.tourBagSettings?.clubHistory ?? false}
                handleEdit={!isOnline ? () => openOfflineEditModal(iron) : () => setIronToEdit(iron)}
                handleCopy={() => setIronToCreate(iron)}
                handleDelete={() => handleDeleteIron(iron.tourBagIronDetailId)}
                handleHistory={() => handleIronForHistory(iron)}
                handleAddToSet={() => {
                  setShowAddIronToSetModal(true);
                  setIronToAddToSet(iron);
                }}
              >
                {renderIron({
                  iron,
                  pageReadability: tourPageSettings?.iron,
                  onFinalHWClick: (iron) => {
                    setFinalHWIron(iron);
                    setShowFinalHWModal(true);
                  },
                })}
              </Accordion>
            );
          })}
      {showAddIronToSetModal && ironToAddToSet && (
        <AddIronToSetModal
          iron={ironToAddToSet}
          ironSets={ironSets}
          handleAddIronToSet={handleAddIronToSet}
          showAddIronToSetModal={showAddIronToSetModal}
          closeAddIronToSetModal={() => setShowAddIronToSetModal(false)}
        />
      )}

      {showFinalHWModal && finalHWIron && <FinalHeadWeightModal showFinalHWModal={showFinalHWModal} closeFinalHWModal={() => setShowFinalHWModal(false)} iron={finalHWIron} />}
      <OfflineModal onCancel={() => setShowPreEditOfflineModal(false)} onClose={showEditFormOfflineModal} isOpen={showPreEditOfflineModal} modalType={'IsOffline'} />
      <OfflineModal onCancel={() => {}} onClose={handleClosePostEditOfflineModal} isOpen={showPostEditOfflineModal} modalType={'ConfirmSave'} />
    </>
  );
}

export default IronList;
